import React from 'react';
import { useParams, Navigate, Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import {
  Phone, PhoneCall, Voicemail, Calendar, CalendarCheck, Clock, MapPin, Star,
  MessageSquare, Bell, CreditCard, ShoppingBag, ShoppingCart, Truck, Wrench,
  HardHat, UtensilsCrossed, Scissors, Dumbbell, Users, UserPlus, TrendingUp,
  BarChart3, Search, Globe, Megaphone, Camera, Image, Sparkles, ClipboardCheck,
  FileText, Mail, Bot, Zap, Repeat, Heart, Award, ThumbsUp, CheckCircle,
  DollarSign, Receipt, Package, Store, Smartphone, Tag, Gift, ArrowRight, Quote
} from 'lucide-react';
import { INDUSTRIES } from '../data/industriesData';
import './IndustryPage.css';

const ICONS = {
  Phone, PhoneCall, Voicemail, Calendar, CalendarCheck, Clock, MapPin, Star,
  MessageSquare, Bell, CreditCard, ShoppingBag, ShoppingCart, Truck, Wrench,
  HardHat, UtensilsCrossed, Scissors, Dumbbell, Users, UserPlus, TrendingUp,
  BarChart3, Search, Globe, Megaphone, Camera, Image, Sparkles, ClipboardCheck,
  FileText, Mail, Bot, Zap, Repeat, Heart, Award, ThumbsUp, CheckCircle,
  DollarSign, Receipt, Package, Store, Smartphone, Tag, Gift
};

const EASE = [0.22, 0.61, 0.36, 1];

export default function IndustryPage() {
  const { slug } = useParams();
  const reduce = useReducedMotion();
  const data = INDUSTRIES.find((i) => i.slug === slug);

  if (!data) return <Navigate to="/industries" replace />;

  const Icon = ICONS[data.icon] || Sparkles;
  const { hero, painsTitle, painsSub, pains, solutionsTitle, solutionsSub, solutions, stats, quote, cta } = data;

  const rise = (d = 0) =>
    reduce
      ? {}
      : {
          initial: { opacity: 0, y: 24 },
          whileInView: { opacity: 1, y: 0 },
          viewport: { once: true, amount: 0.25 },
          transition: { duration: 0.6, delay: d, ease: EASE }
        };

  return (
    <main className="ind-page">
      {/* HERO */}
      <section className="ind-hero">
        <div className="ind-wrap">
          <motion.span className="ind-badge" {...rise(0)}>
            <span className="ind-badge-ic"><Icon size={15} strokeWidth={2.3} /></span>
            {hero.eyebrow}
          </motion.span>
          <motion.h1 className="ind-h1" {...rise(0.06)}>
            {hero.lead} <span className="ind-gold">{hero.accent}</span>.
          </motion.h1>
          <motion.p className="ind-sub" {...rise(0.12)}>{hero.sub}</motion.p>
          <motion.div className="ind-cta-row" {...rise(0.18)}>
            <Link to="/plans" className="ind-btn ind-btn-primary">
              Get started <ArrowRight size={17} strokeWidth={2.4} />
            </Link>
            <Link to="/book" className="ind-btn ind-btn-ghost">Book a call</Link>
          </motion.div>
          <motion.p className="ind-trust" {...rise(0.24)}>Trusted by 100+ local businesses</motion.p>
        </div>
      </section>

      {/* PAINS */}
      <section className="ind-section ind-section-tint">
        <div className="ind-wrap">
          <motion.div className="ind-head" {...rise(0)}>
            <h2 className="ind-h2">{painsTitle}</h2>
            {painsSub && <p className="ind-head-sub">{painsSub}</p>}
          </motion.div>
          <div className="ind-pains-grid">
            {pains.map((p, i) => (
              <motion.div className="ind-pain" key={p.title} {...rise(i * 0.06)}>
                <span className="ind-pain-num">{String(i + 1).padStart(2, '0')}</span>
                <h3 className="ind-pain-title">{p.title}</h3>
                <p className="ind-pain-text">{p.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SOLUTIONS */}
      <section className="ind-section">
        <div className="ind-wrap">
          <motion.div className="ind-head" {...rise(0)}>
            <span className="ind-eyebrow">How Versa Launch helps</span>
            <h2 className="ind-h2">{solutionsTitle}</h2>
            {solutionsSub && <p className="ind-head-sub">{solutionsSub}</p>}
          </motion.div>
          <div className="ind-sol-grid">
            {solutions.map((s, i) => {
              const Si = ICONS[s.icon] || Sparkles;
              return (
                <motion.div className="ind-sol" key={s.title} {...rise((i % 3) * 0.06)}>
                  <span className="ind-sol-ic"><Si size={24} strokeWidth={2} /></span>
                  <h3 className="ind-sol-title">{s.title}</h3>
                  <p className="ind-sol-text">{s.text}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="ind-section ind-section-dark">
        <div className="ind-wrap">
          <div className="ind-stats-grid">
            {stats.map((st, i) => (
              <motion.div key={st.label} {...rise(i * 0.08)}>
                <div className="ind-stat-v">{st.value}</div>
                <div className="ind-stat-l">{st.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* QUOTE */}
      <section className="ind-section">
        <div className="ind-wrap">
          <motion.div className="ind-quote" {...rise(0)}>
            <Quote className="ind-quote-mark" size={40} strokeWidth={2} />
            <p className="ind-quote-text">{quote.text}</p>
            <div className="ind-quote-who">
              <span className="ind-quote-author">{quote.author}</span>
              <span className="ind-quote-role"> &middot; {quote.role}</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="ind-section ind-final">
        <div className="ind-wrap">
          <motion.h2 className="ind-final-title" {...rise(0)}>{cta.title}</motion.h2>
          <motion.p className="ind-final-text" {...rise(0.06)}>{cta.text}</motion.p>
          <motion.div className="ind-cta-row" {...rise(0.12)}>
            <Link to="/plans" className="ind-btn ind-btn-onfill">
              Get started <ArrowRight size={17} strokeWidth={2.4} />
            </Link>
            <Link to="/book" className="ind-btn ind-btn-onfill-ghost">Book a call</Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
}
